<template>
  <div class="dossier-nav">
    <template v-if="$store.state.online && width >= 500">
      <!-- Botão principal: comportamento original preservado -->
      <v-btn
        class="btn btn-help-menu enhanced-help-btn"
        small
        @click="handleToDossie"
      >
        <i class="fas fa-file-alt mr-2"></i> DOSSIÊ
      </v-btn>

      <!-- Botão de alerta: só existe quando há documentos em atenção -->
      <button
        v-if="alerts.length"
        ref="trigger"
        type="button"
        class="dossier-nav__alert-btn"
        :class="`dossier-nav__alert-btn--${severity}`"
        :aria-expanded="open ? 'true' : 'false'"
        aria-controls="dossier-nav-panel"
        :aria-label="triggerLabel"
        :title="triggerLabel"
        @click="toggle"
      >
        <i
          :class="
            severity === 'expired'
              ? 'fas fa-times-circle'
              : 'fas fa-exclamation-triangle'
          "
          aria-hidden="true"
        ></i>
        <span class="dossier-nav__count">{{ alerts.length }}</span>
      </button>

      <!-- Painel com a lista de alertas -->
      <transition name="dossier-nav__fade">
        <div
          v-if="open"
          id="dossier-nav-panel"
          class="dossier-nav__panel"
          role="region"
          aria-label="Documentos que precisam de atenção"
          @keydown.esc="close(true)"
        >
          <div class="dossier-nav__panel-header">
            <i class="fas fa-bell mr-1" aria-hidden="true"></i>
            Documentos que precisam de atenção
          </div>

          <ul class="dossier-nav__list">
            <li v-for="alert in alerts" :key="alert.moduleKey">
              <button
                ref="items"
                type="button"
                class="dossier-nav__item"
                :class="`dossier-nav__item--${alert.type}`"
                @click="goToModule(alert.moduleKey)"
              >
                <i
                  :class="alert.icon"
                  class="dossier-nav__item-icon"
                  aria-hidden="true"
                ></i>
                <span class="dossier-nav__item-text">
                  <span class="dossier-nav__item-name">{{ alert.label }}</span>
                  <span class="dossier-nav__item-info">{{ alert.info }}</span>
                </span>
                <i
                  class="fas fa-chevron-right dossier-nav__item-arrow"
                  aria-hidden="true"
                ></i>
              </button>
            </li>
          </ul>

          <button
            type="button"
            class="dossier-nav__footer"
            @click="openDossierFromPanel"
          >
            <i class="fas fa-folder-open mr-1" aria-hidden="true"></i>
            Abrir dossiê completo
          </button>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import routes from "../../routes";
import permission from "../../lib/permission";
import { buildDossierAlerts } from "../../lib/dossierStatus";
import { fetchDossierAlertsData } from "../../services/dossierAlertsService";

// ⚠️ Confirme o nome da rota da tela DossierEdit
const DOSSIER_EDIT_ROUTE = "dossier-edit";

export default {
  name: "Dossie",

  data() {
    return {
      width: window.innerWidth,
      open: false,
      summary: {},
      hidden: [],
    };
  },

  computed: {
    alerts() {
      return buildDossierAlerts(this.summary, this.hidden);
    },

    // Pior caso define a cor: basta 1 vencido para ficar vermelho
    severity() {
      return this.alerts.some((a) => a.type === "expired")
        ? "expired"
        : "expiring";
    },

    triggerLabel() {
      const n = this.alerts.length;
      const expired = this.alerts.filter((a) => a.type === "expired").length;
      const base = `${n} documento${n > 1 ? "s" : ""} do dossiê precisa${
        n > 1 ? "m" : ""
      } de atenção`;
      return expired
        ? `${base} (${expired} vencido${expired > 1 ? "s" : ""})`
        : base;
    },
  },

  watch: {
    // Fecha o painel ao trocar de tela
    $route() {
      this.open = false;
    },
    // Recarrega quando a conexão volta
    "$store.state.online"(isOnline) {
      if (isOnline) this.loadAlerts();
    },
  },

  created() {
    window.addEventListener("resize", this.handleOnResize);
    this.$root.$on("dossier:updated", this.reloadAlerts);
    this.loadAlerts();
  },

  mounted() {
    document.addEventListener("mousedown", this.onDocumentClick);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleOnResize);
    document.removeEventListener("mousedown", this.onDocumentClick);
    this.$root.$off("dossier:updated", this.reloadAlerts);
  },

  methods: {
    permission,

    handleOnResize() {
      this.width = window.innerWidth;
    },

    handleToDossie() {
      if (routes.currentRoute.name !== "dossier") {
        routes.push({ name: "dossier" });
      }
    },

    // ── Dados ────────────────────────────────────────────────────
    async loadAlerts(force = false) {
      if (!this.$store.state.online) return;
      try {
        const { summary, hidden } = await fetchDossierAlertsData({ force });
        this.summary = summary;
        this.hidden = hidden;
      } catch (err) {
        // Falha silenciosa: o alerta é complementar, não bloqueia a NavBar
        console.error("Dossie.loadAlerts:", err);
      }
    },

    reloadAlerts() {
      this.loadAlerts(true);
    },

    // ── Painel ───────────────────────────────────────────────────
    toggle() {
      this.open = !this.open;
      if (this.open) {
        // Leva o foco ao primeiro item (usuário de teclado)
        this.$nextTick(() => this.$refs.items?.[0]?.focus());
      }
    },

    close(returnFocus = false) {
      this.open = false;
      if (returnFocus) this.$nextTick(() => this.$refs.trigger?.focus());
    },

    onDocumentClick(e) {
      if (this.open && !this.$el.contains(e.target)) this.close();
    },

    // ── Navegação ────────────────────────────────────────────────
    goToModule(moduleKey) {
      this.close();
      // Sem permissão de edição → leva à visão geral
      if (!this.permission("read", "dossier_edit")) {
        this.handleToDossie();
        return;
      }
      routes
        .push({ name: DOSSIER_EDIT_ROUTE, query: { module: moduleKey } })
        .catch(() => {}); // ignora NavigationDuplicated
    },

    openDossierFromPanel() {
      this.close();
      this.handleToDossie();
    },
  },
};
</script>

<style scoped>
/* ── Estilo original do botão (inalterado) ───────────────────── */
.enhanced-help-btn,
.enhanced-update-btn {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0.1) 100%
  ) !important;
  color: white !important;
  border: 2px solid rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  text-transform: uppercase !important;
}

.enhanced-help-btn:hover,
.enhanced-update-btn:hover {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.25) 0%,
    rgba(255, 255, 255, 0.15) 100%
  ) !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2) !important;
  color: white !important;
}

/* ── Container: âncora do painel ─────────────────────────────── */
.dossier-nav {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* ── Botão de alerta ─────────────────────────────────────────── */
.dossier-nav__alert-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 28px; /* mesma altura do v-btn small */
  min-width: 44px;
  padding: 0 10px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-radius: 12px !important;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s, box-shadow 0.15s;
}

.dossier-nav__alert-btn:hover {
  filter: brightness(0.92);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

/* Vermelho + texto branco → contraste ≈ 4.5:1 */
.dossier-nav__alert-btn--expired {
  background: #dc3545;
  color: #fff;
}

/* Laranja + texto escuro → contraste ≈ 9:1 (branco falharia) */
.dossier-nav__alert-btn--expiring {
  background: #fb8b01;
  color: #1a1a1a;
}

.dossier-nav__alert-btn:focus-visible,
.dossier-nav__item:focus-visible,
.dossier-nav__footer:focus-visible {
  outline: 3px solid #1f5a76;
  outline-offset: 2px;
}

.dossier-nav__count {
  line-height: 1;
}

/* ── Painel ──────────────────────────────────────────────────── */
.dossier-nav__panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 1000;
  width: 340px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  text-align: left;
}

.dossier-nav__panel-header {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #444;
  padding: 10px 16px;
  background: #f8f8f7;
  border-bottom: 1px solid #e8e8e8;
}

.dossier-nav__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 60vh;
  overflow-y: auto;
}

/* ── Item ────────────────────────────────────────────────────── */
.dossier-nav__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 10px 16px;
  background: #fff;
  border: none;
  border-bottom: 1px solid #f0f0f0;
  border-left: 4px solid transparent;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

.dossier-nav__item:hover {
  background: #f5f9fb;
}

.dossier-nav__item--expired {
  border-left-color: #dc3545;
}
.dossier-nav__item--expired .dossier-nav__item-icon,
.dossier-nav__item--expired .dossier-nav__item-info {
  color: #842029; /* contraste AA sobre branco */
}

.dossier-nav__item--expiring {
  border-left-color: #fb8b01;
}
.dossier-nav__item--expiring .dossier-nav__item-icon,
.dossier-nav__item--expiring .dossier-nav__item-info {
  color: #856404; /* contraste AA sobre branco */
}

.dossier-nav__item-icon {
  font-size: 16px;
  flex-shrink: 0;
}

.dossier-nav__item-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.dossier-nav__item-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
}

.dossier-nav__item-info {
  font-size: 12px;
  font-weight: 600;
}

.dossier-nav__item-arrow {
  font-size: 11px;
  color: #bbb;
}

.dossier-nav__item:hover .dossier-nav__item-arrow {
  color: #1f5a76;
}

/* ── Rodapé ──────────────────────────────────────────────────── */
.dossier-nav__footer {
  display: block;
  width: 100%;
  min-height: 44px;
  padding: 10px 16px;
  background: #f8f8f7;
  border: none;
  border-top: 1px solid #e8e8e8;
  color: #1f5a76;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.dossier-nav__footer:hover {
  background: #eef5f8;
}

/* ── Transição ───────────────────────────────────────────────── */
.dossier-nav__fade-enter-active,
.dossier-nav__fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dossier-nav__fade-enter,
.dossier-nav__fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .dossier-nav__fade-enter-active,
  .dossier-nav__fade-leave-active {
    transition: none;
  }
}
</style>